// ── Migration Visibility Guard ──
// Canonical gate that hides non-approved imported jobs from every
// operational surface (opportunities, Smart Dispatch, scheduling,
// install today, offer creation). Native Koredor jobs (no sourceMeta)
// are always considered approved — this guard ONLY filters imports.
//
// Wire this resolver, never inline `job.sourceMeta?.migrationStatus`.
//
// Anchor: mem://features/jobber-import-bridge (beta)

import type { Job, MigrationBypassStage } from "../types";

function hasImportedJobNumber(job: unknown): boolean {
  const rec = job as { jobNumber?: string; id?: string };
  const raw = String(rec?.jobNumber ?? rec?.id ?? "").trim().toUpperCase();
  return raw.startsWith("JOB-IMP-");
}

export function isMigrationApproved(job: Pick<Job, "sourceMeta">): boolean {
  const meta = job.sourceMeta;
  if (!meta) return true; // native default; imported rows without sourceMeta also pass by design
  return meta.migrationStatus === "approved";
}

export function isMigrationPending(job: Pick<Job, "sourceMeta">): boolean {
  return job.sourceMeta?.migrationStatus === "needs_review";
}

export function isMigrationRejected(job: Pick<Job, "sourceMeta">): boolean {
  return job.sourceMeta?.migrationStatus === "rejected";
}

/**
 * Canonical readiness ownership resolver for imported jobs.
 * Back-compat: legacy Jobber imports without `readinessMode` are treated as external-managed.
 */
export function isExternalManagedImport(job: Pick<Job, "sourceMeta">): boolean {
  const meta = job.sourceMeta;
  if (!meta) return hasImportedJobNumber(job);
  // Canonical source-system signal takes precedence so a bad/missing
  // readinessMode cannot accidentally push imported jobs back into native gates.
  const source = String(meta.sourceSystem ?? "").trim().toLowerCase();
  if (source !== "" && source !== "koredor") return true;
  if (meta.readinessMode) return meta.readinessMode === "external_managed";
  return hasImportedJobNumber(job);
}

export function isNativeManagedReadiness(job: Pick<Job, "sourceMeta">): boolean {
  return !isExternalManagedImport(job);
}

// Stages bypassed for legacy migrated jobs that have no explicit bypassedStages set.
// "permitting" is intentionally excluded — permitting is now required on all jobs,
// migrated or otherwise.
const LEGACY_BYPASS_DEFAULTS: MigrationBypassStage[] = ["build_review", "ordering"];

export function migrationBypasses(
  job: Pick<Job, "id" | "jobNumber" | "sourceMeta">,
  stage: MigrationBypassStage,
): boolean {
  if (!isExternalManagedImport(job)) return false;
  if (job.sourceMeta?.migrationStatus !== "approved") return false;
  const stages = job.sourceMeta?.bypassedStages;
  // Legacy jobs with no bypassedStages fall back to the limited default — not all stages.
  return !stages ? LEGACY_BYPASS_DEFAULTS.includes(stage) : stages.includes(stage);
}

export function filterMigrationApproved<T extends Pick<Job, "sourceMeta">>(jobs: T[]): T[] {
  return jobs.filter(isMigrationApproved);
}

/** Jobs awaiting operator review (drives /import/review-queue surface). */
export function filterPendingReview<T extends Pick<Job, "sourceMeta">>(jobs: T[]): T[] {
  return jobs.filter(isMigrationPending);
}
